import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";

const AudioContext = createContext(null);

// Module-level singleton: the entire website gets exactly ONE Audio element,
// immune to React StrictMode double-mounts or provider remounts. This is what
// guarantees THE DUBAU can never play twice at the same time.
let singletonEl = null;
const TAP_EVENTS = ["pointerdown", "touchstart", "keydown"];

function getSingletonAudio(src) {
  if (!singletonEl) {
    singletonEl = new Audio();
    singletonEl.preload = "metadata";
    singletonEl.src = src || "";
    singletonEl.load();
  }
  return singletonEl;
}

/** ONE autoplay attempt with sound; if the browser blocks it, start on the visitor's first tap anywhere. Armed at most once per element. */
function initAutoplay(el, onStarted) {
  if (el.__cwAutoplayAttempted) return;
  el.__cwAutoplayAttempted = true;
  el.play()
    .then(() => onStarted())
    .catch(() => {
      if (el.__cwTapArmed) return;
      el.__cwTapArmed = true;
      const startOnTap = () => {
        TAP_EVENTS.forEach((e) => document.removeEventListener(e, startOnTap));
        el.play().then(() => onStarted()).catch(() => {});
      };
      TAP_EVENTS.forEach((e) => document.addEventListener(e, startOnTap, { passive: true }));
    });
}

/**
 * Site-wide persistent player. The single <audio> element lives here, ABOVE
 * the router, so playback never restarts when visitors change pages.
 * Only artist-owned audio files are played — streaming services stay in
 * their own official embeds/links.
 *
 * Autoplay behavior: the exclusive track is attempted with sound as soon as
 * the site loads. If the browser blocks unmuted autoplay, the player stays
 * ready at 0:00 with a normal Play button, and the song starts on the
 * visitor's first tap anywhere on the page — then keeps playing while they
 * browse. The Audio element is a module-level singleton: exactly one instance
 * exists for the whole site, so the song can never double up.
 */
export function AudioProvider({ tracks, children }) {
  const audioRef = useRef(null);
  const [index, setIndex] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [expanded, setExpanded] = useState(false);
  const [volume, setVolume] = useState(0.85);
  const [unavailable, setUnavailable] = useState(false);

  useEffect(() => {
    const el = getSingletonAudio(tracks[0]?.src);
    audioRef.current = el;
    const onTime = () => setProgress(el.currentTime || 0);
    const onMeta = () => setDuration(el.duration || 0);
    const onEnd = () => setPlaying(false);
    const onErr = () => {
      setPlaying(false);
      setUnavailable(true);
    };
    el.addEventListener("timeupdate", onTime);
    el.addEventListener("loadedmetadata", onMeta);
    el.addEventListener("ended", onEnd);
    el.addEventListener("error", onErr);

    // Autoplay is owned by the ONE visible player and the ONE shared element.
    // Never paused from React lifecycle — only the user's Play/Pause pauses it.
    initAutoplay(el, () => setPlaying(true));

    return () => {
      el.removeEventListener("timeupdate", onTime);
      el.removeEventListener("loadedmetadata", onMeta);
      el.removeEventListener("ended", onEnd);
      el.removeEventListener("error", onErr);
    };
  }, []);

  const play = useCallback(
    async (i) => {
      const el = audioRef.current;
      if (!el || !tracks.length) return;
      const nextIndex = typeof i === "number" ? i : index;
      const track = tracks[nextIndex];
      if (!track) return;
      if (el.src !== window.location.origin + track.src) {
        el.src = track.src;
        setUnavailable(false);
        setProgress(0);
        setIndex(nextIndex);
      }
      try {
        await el.play();
        setPlaying(true);
      } catch {
        setPlaying(false);
      }
    },
    [index, tracks],
  );

  const toggle = useCallback(() => {
    const el = audioRef.current;
    if (!el || !tracks.length) return;
    if (playing) {
      el.pause();
      setPlaying(false);
    } else {
      play();
    }
  }, [playing, play, tracks.length]);

  const seek = useCallback(
    (t) => {
      const el = audioRef.current;
      if (el) el.currentTime = t;
      setProgress(t);
    },
    [],
  );

  const changeVolume = useCallback((v) => {
    const el = audioRef.current;
    setVolume(v);
    if (el) el.volume = v;
  }, []);

  const track = tracks[index] || null;

  return (
    <AudioContext.Provider
      value={{
        track,
        tracks,
        index,
        playing,
        progress,
        duration,
        expanded,
        volume,
        unavailable,
        play,
        toggle,
        seek,
        changeVolume,
        setExpanded,
      }}
    >
      {children}
    </AudioContext.Provider>
  );
}

export const usePlayer = () => useContext(AudioContext);
